import * as Sentry from '@sentry/react'

const DSN = import.meta.env.REACT_APP_SENTRY_DSN
const ENVIRONMENT = import.meta.env.REACT_APP_ENVIRONMENT ?? import.meta.env.MODE

/**
 * Parâmetros de query cujo valor pode ir para o Sentry. Tudo que não estiver aqui tem o
 * valor trocado por [filtrado].
 *
 * É uma lista de permissão, e não de bloqueio, de propósito: as telas de busca mandam
 * nome, e-mail e CPF de cliente na URL (`/customers?query=maria`), e a URL viaja em todo
 * evento e breadcrumb. Esquecer de adicionar um parâmetro novo aqui custa contexto de
 * depuração; esquecer de bloquear um parâmetro novo custaria dado pessoal.
 */
const SAFE_QUERY_PARAMS = new Set ([
  'page', 'per_page', 'order', 'mode', 'status', 'active', 'canceled', 'bill_active',
  'date', 'start_date', 'end_date', 'month', 'year', 'time',
  'business_id', 'professional_id', 'customer_id', 'member_id', 'company_id',
  'bill_id', 'category_id', 'service_id', 'product_id', 'bundle_id',
  'cutilei_schedules', 'show_all_professionals', 'user_type', 'app_version'
])

export function scrubUrl (url) {
  if (typeof url !== 'string') return url

  const separator = url.indexOf ('?')

  if (separator === -1) return url

  const path = url.slice (0, separator)
  const query = url.slice (separator + 1)
    .split ('&')
    .map (pair => {
      const name = pair.split ('=')[0]
      return SAFE_QUERY_PARAMS.has (name) ? pair : `${name}=[filtrado]`
    })
    .join ('&')

  return `${path}?${query}`
}

/**
 * O corpo da requisição carrega senha no login, e o header carrega o Bearer vivo — os
 * dois motivos que fizeram o P0.2 existir. Nenhum dos dois pode sair do navegador.
 */
function beforeSend (event) {
  if (event.request) {
    delete event.request.data
    delete event.request.headers
    delete event.request.cookies
    delete event.request.query_string

    event.request.url = scrubUrl (event.request.url)
  }

  return event
}

function beforeBreadcrumb (breadcrumb) {
  // O breadcrumb de console reproduz o argumento inteiro de qualquer console.* da
  // página, e ainda há 45 deles fora do logApiError.
  if (breadcrumb.category === 'console') return null

  if (breadcrumb.data?.url) breadcrumb.data.url = scrubUrl (breadcrumb.data.url)

  return breadcrumb
}

export function initErrorReporting () {
  // Sem DSN configurado o Sentry fica inerte. É o estado do repositório até o projeto
  // ser criado no painel e a variável entrar nos secrets do deploy.
  if (!DSN) return

  Sentry.init ({
    dsn: DSN,
    environment: ENVIRONMENT,
    sendDefaultPii: false,
    // Sem tracing: o que falta hoje é enxergar erro, e amostragem de performance
    // consome a cota sem responder a essa pergunta.
    tracesSampleRate: 0,
    beforeSend,
    beforeBreadcrumb
  })
}

export function reportError (error, { context, status } = {}) {
  if (!DSN) return

  Sentry.captureException (error, {
    tags: {
      context: context || 'api',
      api_status: status ?? 'network'
    }
  })
}

/**
 * Opções de erro do `createRoot`. O React 19 não relança mais erro de render: o que nenhum
 * ErrorBoundary pega vai para `window.reportError`, e o erro de que ele se recupera sozinho
 * vai só para o console. Com os ganchos, os dois chegam ao Sentry com a pilha de componentes.
 *
 * Não há `onCaughtError` de propósito. O `Sentry.ErrorBoundary` (components/ErrorBoundary) já
 * reporta o que pega, e o gancho mandaria o mesmo erro duas vezes; é a combinação que o
 * README do `@sentry/react` recomenda.
 *
 * Sem DSN não há opção nenhuma: o React mantém o comportamento padrão.
 */
export function rootErrorOptions() {
  if (!DSN) return {}

  return {
    // O gancho substitui o relato padrão do React; o callback mantém o erro no console.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.error(error, errorInfo?.componentStack)
    }),
    onRecoverableError: Sentry.reactErrorHandler()
  }
}
